import React from "react"
import { graphql } from "gatsby"
import { useSanity } from "@sr/common"
import { App, Page, Seo } from "@sr/gatsby-theme-ui"
import HardwareCategoryHero from "../components/Section/Hardware/Category/HardwareCategoryHero"
import HardwareCategoryOperationalBenefits from "../components/Section/Hardware/Category/HardwareCategoryOperationalBenefits"
import HardwareCategoryEducationalInfo from "../components/Section/Hardware/Category/HardwareCategoryEducationalInfo"
import HardwareCategoryFaq from "../components/Section/Hardware/Category/HardwareCategoryFaq"
import HardwareAlloy from "../components/Section/Hardware/HardwareAlloy"
import HardwareIntegrations from "../components/Section/Hardware/HardwareIntegrations"
import HardwareCompatibleHardware from "../components/Section/Hardware/HardwareCompatibleHardware"
import HardwareDemo from "../components/Section/Hardware/HardwareDemo"
import HardwareCategoryTrustedPartners from "../components/Section/Hardware/Category/HardwareCategoryTrustedPartners"

const TemplateHardwareCategory = ({ data, pageContext, location }: any) => {
  if (!data) return null
  const {
    breadcrumb: { crumbs },
  } = pageContext

  const {
    title,
    hero,
    operationalBenefits,
    educationalInfo,
    faq,
    alloy,
    integrations,
    compatibleHardware,
    trustedPartners,
    demo,
    path,
  } = data?.sanityHardwareCategoriesPage
  const hardwareCategories = data?.allSanityHardwareCategoriesPage?.nodes

  return (
    <App crumbs={crumbs} location={location} crumbLabel={title}>
      <Page>
        <HardwareCategoryHero hero={hero} />
        <HardwareCategoryOperationalBenefits
          operationalBenefits={operationalBenefits}
        />
        <HardwareCategoryEducationalInfo educationalInfo={educationalInfo} />
        <HardwareCategoryFaq faq={faq} />
        {alloy && <HardwareAlloy />}
        {integrations && <HardwareIntegrations modifiedPadding={true} />}
        {compatibleHardware && (
          <HardwareCompatibleHardware
            bgColor="var(--color-gray050)"
            hardwareCategories={hardwareCategories}
            path={path.current}
          />
        )}
        {trustedPartners && <HardwareCategoryTrustedPartners />}
        <HardwareDemo demo={demo} />
      </Page>
    </App>
  )
}

export default TemplateHardwareCategory

export const Head = ({ data }: any) => {
  const { imgBuilder } = useSanity()
  const sanitySeoData = data?.sanityHardwareCategoriesPage?.seo
  let metaArray = []

  if (data?.sanityHardwareCategoriesPage?.ogImage != undefined) {
    const src = imgBuilder(data?.sanityHardwareCategoriesPage?.ogImage?.asset)
      .width(1200)
      .height(627)
      .auto("format")
      .crop("focalpoint")
      .url()
    metaArray.push({
      property: "og:image",
      content: src,
    })
  }

  return sanitySeoData ? (
    <Seo
      title={sanitySeoData?.seoTools?.seo_title || ""}
      noIndex={sanitySeoData?.noindex || false}
      canonical={sanitySeoData?.canonical || ""}
      keywords={sanitySeoData?.seoTools?.focus_keyword || ""}
      description={sanitySeoData?.seoTools?.meta_description || ""}
      meta={metaArray.length ? metaArray : []}
    />
  ) : null
}

export const HARDWARE_CATEGORY_QUERY = graphql`
  query HardwareCategoryQuery($path_context: String!) {
    sanityHardwareCategoriesPage(path: { current: { eq: $path_context } }) {
      title
      path {
        current
      }
      seo {
        noindex
        canonical
        seoTools {
          focus_keyword
          focus_synonyms
          meta_description
          seo_title
        }
      }
      ogImage {
        asset {
          _id
        }
      }
      hero {
        title
        _rawText(resolveReferences: { maxDepth: 10 })
        img {
          ...SanityImageWithPreview
          alt
        }
        buttonText
        buttonLink {
          ...SanityBuilderInternalLink
        }
      }
      operationalBenefits {
        title
        contentSections {
          sectionImg {
            ...SanityImageWithPreview
            alt
          }
          benefitTitle
          _rawText(resolveReferences: { maxDepth: 10 })
        }
      }
      educationalInfo {
        title
        text
        img {
          ...SanityImageWithPreview
          alt
        }
        contentSections {
          label
          title
          text
        }
      }
      faq {
        title
        _rawBody(resolveReferences: { maxDepth: 10 })
      }
      alloy
      integrations
      compatibleHardware
      trustedPartners
      demo {
        title
        buttonText
        buttonLink {
          ...SanityBuilderInternalLink
        }
      }
    }
    allSanityHardwareCategoriesPage {
      nodes {
        path {
          current
        }
        icon {
          ...SanityImageWithPreview
          alt
        }
        cardTitle
      }
    }
  }
`
